import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Header from '../../components/Layout/Header';
import Sidebar from '../../components/Layout/Sidebar';
import { feeCategoryService } from '../../services/apiService';
import { showToast } from '../../utils/toast';
import { getAvatarColor } from '../../utils/avatar';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Avatar from '@mui/material/Avatar';
import { Box as MuiBox } from '@mui/system';

const defaultForm = {
  id: '',
  name: '',
  description: '',
  frequency: 'monthly',
  charge_scope: 'class',
  is_active: true,
};

const FeeCategories = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const drawerWidth = 280;

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleDrawerToggle = () => {
    setSidebarOpen((prev) => !prev);
  };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await feeCategoryService.getAll();
      if (!response?.success) {
        throw new Error(response?.error || 'Failed to fetch categories');
      }
      setCategories(response.data || []);
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Unable to load categories';
      setError(message);
      showToast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const handleDialogClose = () => {
    if (saving) return;
    setDialogOpen(false);
    setForm(defaultForm);
  };

  const handleOpenCreate = () => {
    setForm(defaultForm);
    setDialogOpen(true);
  };

  const handleEdit = (category) => {
    setForm({
      ...category,
      is_active: Boolean(category.is_active),
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this fee category?')) return;
    try {
      await feeCategoryService.delete(id);
      showToast.success('Category deleted');
      setCategories((prev) => prev.filter((item) => item.id !== id));
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to delete category';
      showToast.error(message);
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      showToast.error('Name is required');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        name: form.name.trim(),
        description: form.description || null,
        frequency: form.frequency,
        charge_scope: form.charge_scope,
        is_active: form.is_active ? 1 : 0,
      };
      if (form.id) {
        await feeCategoryService.update(form.id, payload);
        showToast.success('Category updated');
      } else {
        const res = await feeCategoryService.create(payload);
        showToast.success('Category created');
        if (res?.data?.id) {
          payload.id = res.data.id;
        }
      }
      handleDialogClose();
      loadCategories();
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Save failed';
      showToast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const activeCount = useMemo(() => categories.filter((cat) => cat.is_active).length, [categories]);

  // Deterministic dark color from a string (category name)

  return (
    <>
      <Box sx={{ display: 'flex' }}>
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} drawerWidth={drawerWidth} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            // On mobile: always full width, no margin (sidebar overlays)
            // On desktop: adjust width and margin based on sidebar state
            ml: { xs: 0, md: `${0}px` },
            backgroundColor: 'background.default',
          }}
        >
          <Header onMenuClick={handleDrawerToggle} />
          <Box
            sx={{
              p: { xs: 2, sm: 3 },
              mt: '64px',
              minHeight: 'calc(100vh - 64px)',
            }}
          >
            <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, border: 1, borderColor: 'divider', mb: 3 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    Fee Categories
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Manage the catalogue of fee types available to assign to classes or individual students.
                  </Typography>
                </Box>
                <Button variant="contained" sx={{ ml: { md: 'auto' } }} onClick={handleOpenCreate} startIcon={<AddIcon />}>
                  Add Category
                </Button>
              </Stack>
            </Paper>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Paper elevation={0} sx={{ borderRadius: 3, border: 1, borderColor: 'divider', p: { xs: 2, sm: 3 } }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : categories.length === 0 ? (
                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                  No categories found. Click "Add Category" to create one.
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    gap: { xs: 1.5, sm: 2 },
                  }}
                >
                  {categories.map((category) => (
                    <Paper
                      key={category.id}
                      variant="outlined"
                      sx={{
                        p: { xs: 2, sm: 2.4 },
                        borderRadius: 2,
                        borderColor: 'divider',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        position: 'relative',
                      }}
                    >
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-start' }} justifyContent="space-between" flexWrap="nowrap">
                        {/* Left: Avatar + Details */}
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                          <Avatar sx={{ width: 48, height: 48, bgcolor: getAvatarColor(category.name), color: '#ffffff', fontWeight: 700 }}>
                            {(category.name || 'F').trim().charAt(0).toUpperCase()
                            }
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle1" fontWeight={700} noWrap>
                              {category.name}
                            </Typography>
                            {category.description ? (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.25 }} noWrap>
                                {category.description}
                              </Typography>
                            ) : null}
                            <Typography variant="body2" color="text.secondary" noWrap>
                              <strong>Frequency:</strong> {(category.frequency || 'monthly').replace(/^[a-z]/, (c) => c.toUpperCase())} &nbsp;·&nbsp; <strong>Scope:</strong> {category.charge_scope === 'student' ? 'Student' : 'Class'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" noWrap>
                              <strong>Status:</strong> {category.is_active ? 'Active' : 'Inactive'}
                            </Typography>
                          </Box>
                        </Stack>

                        {/* Right: Actions */}
                        <Stack direction={{ xs: 'row', sm: 'column' }} spacing={1} alignItems={{ xs: 'center', sm: 'flex-end' }} justifyContent={{ xs: 'flex-end', sm: 'flex-start' }} sx={{ flex: '0 0 auto', minWidth: { xs: 'auto', sm: '100px' } }}>
                          <Button size="small" startIcon={<EditIcon />} onClick={() => handleEdit(category)}>
                            Edit
                          </Button>
                          <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => handleDelete(category.id)}>
                            Delete
                          </Button>
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
                </Box>
              )}
              <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary">
                  Active categories: {activeCount}
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Box>

        <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
          <DialogTitle>{form.id ? 'Edit Fee Category' : 'Add Fee Category'}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Name" value={form.name} onChange={handleChange('name')} size="small" fullWidth />
              <TextField
                label="Description"
                value={form.description}
                onChange={handleChange('description')}
                size="small"
                multiline
                minRows={2}
                fullWidth
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="frequency-label">Frequency</InputLabel>
                  <Select
                    labelId="frequency-label"
                    label="Frequency"
                    value={form.frequency}
                    onChange={handleChange('frequency')}
                  >
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="annual">Annual</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel id="scope-label">Charge Scope</InputLabel>
                  <Select
                    labelId="scope-label"
                    label="Charge Scope"
                    value={form.charge_scope}
                    onChange={handleChange('charge_scope')}
                  >
                    <MenuItem value="class">Class</MenuItem>
                    <MenuItem value="student">Student</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="body2">Active</Typography>
                <Switch checked={form.is_active} onChange={handleChange('is_active')} />
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} disabled={saving}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : null}>
              {form.id ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};
export default FeeCategories;
